import { orderBy } from "lodash-es";

import type { AppStore } from "@/store/AppStore";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models";
import { clientEnvModule } from "@/modules/client-env";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import {
  combineSequentialTranscriptTurnGroups,
  getNextTranscriptDisplayTurnOffset,
  offsetLiveTranscriptPartials,
  offsetTranscriptTurns,
} from "@/modules/audio-recording/handoff-transcript-turns";
import {
  resolveTranscriptSpeakerCurrentUserContext,
  resolveTranscriptSpeakerParticipantDisplayLabel,
  resolveTranscriptSpeakerPresentation,
  type TranscriptSpeakerCurrentUser,
} from "@/modules/audio-recording/transcript-speaker-identity";
import { resolveAudioRecordingDurationLabel } from "@/modules/audio-recording/recording-duration-label";
import { resolveTranscriptSpeakerBadgeName } from "@/modules/audio-recording/transcript-speaker-badges";
import { normalizeSpeakerParticipantName } from "@/modules/audio-recording/transcript-speaker-labels";
import type {
  TranscriptSpeakerParticipantMetadata,
  TranscriptSpeakerVariant,
} from "@/modules/audio-recording/transcript-types";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import {
  type MediaRecorderTranscriptPartialTurn,
  TranscriptionStatus,
} from "@/store/audio/recording";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds";
import {
  getNoteSuggestionContentReadiness,
  NoteSuggestionContentState,
} from "@/store/note/helpers/note-suggestion-content";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";
import type { AudioCaptureToolbarPrimaryAction } from "@/store/audio/pipeline/audio-capture.types";
import { projectCapturePipelineUiState } from "@/store/audio/pipeline/audio-pipeline.ui-projection";
import type {
  AudioCaptureEnvironmentState,
  AudioCaptureMicrophoneState,
  AudioCaptureRecordingState,
  AudioCaptureTranscriptEntry,
  AudioCaptureTranscriptState,
} from "./audio-capture-actions.types";

const canProcessRecording = ({
  phase,
  isMicrophoneDisconnected,
  canProcessNoteContent,
}: {
  phase: AudioCapturePhase;
  isMicrophoneDisconnected: boolean;
  canProcessNoteContent: boolean;
}): boolean => {
  if (isMicrophoneDisconnected || !canProcessNoteContent) {
    return false;
  }

  if (
    phase === AudioCapturePhase.Paused ||
    phase === AudioCapturePhase.Recording ||
    phase === AudioCapturePhase.Stopped
  ) {
    return true;
  }

  return false;
};

function deriveToolbarPrimaryAction({
  canProcess,
  isProcessing,
  retryTargets,
}: {
  canProcess: boolean;
  isProcessing: boolean;
  retryTargets: {
    recordAgain: boolean;
    upload: boolean;
    retranscribe: boolean;
    transform: boolean;
    retryAll: boolean;
  };
}): AudioCaptureToolbarPrimaryAction {
  if (retryTargets.recordAgain) {
    return {
      kind: "record-again",
      ariaLabel: "Record again",
    };
  }

  if (retryTargets.retryAll) {
    return {
      kind: "retry-all",
      ariaLabel: "Retry all processing",
    };
  }

  if (retryTargets.upload || retryTargets.retranscribe || retryTargets.transform) {
    return {
      kind: "retry",
      ariaLabel: retryTargets.upload ? "Retry upload and processing" : "Retry processing",
    };
  }

  return {
    kind: "done",
    isDisabled: !canProcess || isProcessing,
  };
}

export interface AudioCaptureSourceSnapshot {
  recording: Omit<AudioCaptureRecordingState, "waveform">;
  ui: {
    showOnboardingTooltip: boolean;
    shouldShowDesktopDownloadNotice: boolean;
  };
  environment: AudioCaptureEnvironmentState;
  visualizer: AudioLevelProbe | null;
  recordingId: string | null;
}

export function normalizeTranscriptEntries(
  finalTurns: LocalAudioRecordingTranscriptTurnRecord[],
  livePartialTurns: MediaRecorderTranscriptPartialTurn[] = [],
  {
    currentUser = null,
  }: {
    currentUser?: TranscriptSpeakerCurrentUser | null;
  } = {}
): AudioCaptureTranscriptEntry[] {
  const sortedTurns = orderBy(
    buildDisplayTranscriptTurns({
      finalTurns,
      livePartialTurns,
    }),
    turn => turn.turnOrder,
    "asc"
  );
  const currentUserContext = resolveTranscriptSpeakerCurrentUserContext({
    currentUser,
    speakers: sortedTurns,
  });

  return sortedTurns.map((turn, index) => {
    const rawText = turn.words.map(word => word.text).join(" ");
    const nextTurn = sortedTurns[index + 1];
    const speakerPresentation = resolveTranscriptSpeakerPresentation({
      speakerVariant: turn.speakerVariant,
      channel: turn.channel,
      speakerParticipant: turn.speakerParticipant,
      speakerId: turn.speakerId,
      currentUser: currentUserContext,
    });
    const speakerDisplayName = resolveTranscriptSpeakerBadgeName({
      isSelfSpeaker: speakerPresentation.isSelfSpeaker,
      isTranscriptOnly: speakerPresentation.isTranscriptOnly,
      channel: turn.channel,
      speakerParticipant: turn.speakerParticipant,
    });
    const speakerAttributionPrefix =
      !speakerPresentation.isSelfSpeaker &&
      !speakerPresentation.isTranscriptOnly &&
      !speakerDisplayName
        ? buildSpeakerAttributionPrefix(turn.speakerParticipant)
        : null;
    const nextVisualGroupKey = nextTurn
      ? resolveDisplayTranscriptTurnVisualGroupKey(nextTurn, currentUserContext)
      : null;
    return {
      id: turn.id,
      alignment: speakerPresentation.isTranscriptOnly
        ? MDSBubbleAlignment.Center
        : speakerPresentation.isSelfSpeaker
          ? MDSBubbleAlignment.Right
          : MDSBubbleAlignment.Left,
      variant: speakerPresentation.isTranscriptOnly
        ? MDSBubbleVariant.Transparent
        : speakerPresentation.isSelfSpeaker
          ? MDSBubbleVariant.VoiceSelf
          : MDSBubbleVariant.VoiceOthers,
      speakerGroupKey: speakerPresentation.groupKey,
      ...(speakerDisplayName ? { speakerDisplayName } : {}),
      text: speakerAttributionPrefix ? `${speakerAttributionPrefix} ${rawText}` : rawText,
      isLastInGroup: speakerPresentation.groupKey !== nextVisualGroupKey,
    } satisfies AudioCaptureTranscriptEntry;
  });
}

/** Builds an attribution prefix for participant identity carried on transcript turns. */
function buildSpeakerAttributionPrefix(
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null
): string | null {
  const speakerName = normalizeSpeakerParticipantName(speakerParticipant?.name);
  const speakerEmail = normalizeSpeakerParticipantName(speakerParticipant?.email);
  const speakerAttribution = [speakerName, speakerEmail].filter(Boolean).join(" - ");

  return speakerAttribution ? `[${speakerAttribution}]` : null;
}

const buildFullTranscript = ({
  finalTurns,
  livePartialTurns,
  currentUser,
}: {
  finalTurns: LocalAudioRecordingTranscriptTurnRecord[];
  livePartialTurns: MediaRecorderTranscriptPartialTurn[];
  currentUser?: TranscriptSpeakerCurrentUser | null;
}): string => {
  const displayTurns = orderBy(
    buildDisplayTranscriptTurns({
      finalTurns,
      livePartialTurns,
    }),
    turn => turn.turnOrder,
    "asc"
  );
  if (!displayTurns.length) return "";

  const currentUserContext = resolveTranscriptSpeakerCurrentUserContext({
    currentUser,
    speakers: displayTurns,
  });

  return displayTurns
    .map(turn => {
      const words = turn.words.map(word => word.text).join(" ");
      const speakerPresentation = resolveTranscriptSpeakerPresentation({
        speakerVariant: turn.speakerVariant,
        channel: turn.channel,
        speakerParticipant: turn.speakerParticipant,
        speakerId: turn.speakerId,
        currentUser: currentUserContext,
      });
      const participantDisplayLabel = resolveTranscriptSpeakerParticipantDisplayLabel({
        speakerParticipant: turn.speakerParticipant,
        includeEmail: true,
      });
      const speakerLabel =
        participantDisplayLabel &&
        !speakerPresentation.isTranscriptOnly &&
        !speakerPresentation.isSelfSpeaker
          ? participantDisplayLabel
          : speakerPresentation.displayLabel;
      return `[${speakerLabel}]: ${words}`;
    })
    .join("\n");
};

type DisplayTranscriptTurn = {
  id: string;
  channel: number;
  speakerId?: string | null;
  speakerVariant?: TranscriptSpeakerVariant | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  turnOrder: number;
  words: Array<{ text: string }>;
};

function resolveDisplayTranscriptTurnVisualGroupKey(
  turn: DisplayTranscriptTurn,
  currentUser?: TranscriptSpeakerCurrentUser | null
): string {
  const speakerPresentation = resolveTranscriptSpeakerPresentation({
    speakerVariant: turn.speakerVariant,
    channel: turn.channel,
    speakerParticipant: turn.speakerParticipant,
    speakerId: turn.speakerId,
    currentUser,
  });

  return speakerPresentation.groupKey;
}

const buildDisplayTranscriptTurns = ({
  finalTurns,
  livePartialTurns,
}: {
  finalTurns: LocalAudioRecordingTranscriptTurnRecord[];
  livePartialTurns: MediaRecorderTranscriptPartialTurn[];
}): DisplayTranscriptTurn[] => {
  const turnsByOrder = new Map<number, DisplayTranscriptTurn>();

  for (const partialTurn of livePartialTurns) {
    turnsByOrder.set(partialTurn.turnOrder, {
      id: `partial-${partialTurn.turnOrder}`,
      channel: partialTurn.channel,
      speakerId: partialTurn.speakerId ?? null,
      speakerVariant: partialTurn.speakerVariant ?? null,
      speakerParticipant: partialTurn.speakerParticipant ?? null,
      turnOrder: partialTurn.turnOrder,
      words: (partialTurn.words ?? []).map(word => ({
        text: word.word,
      })),
    });
  }

  for (const finalTurn of finalTurns) {
    turnsByOrder.set(finalTurn.transcriptTurnIndex, {
      id: finalTurn.id,
      channel: finalTurn.channel,
      speakerId: finalTurn.content.speakerId ?? null,
      speakerVariant: finalTurn.content.speakerVariant ?? null,
      speakerParticipant: finalTurn.content.speakerParticipant ?? null,
      turnOrder: finalTurn.transcriptTurnIndex,
      words: (finalTurn.content?.words ?? []).map(word => ({
        text: word.text,
      })),
    });
  }

  return [...turnsByOrder.values()];
};

export function createAudioCaptureSourceSnapshot(
  store: AppStore,
  { noteId }: { noteId: string }
): AudioCaptureSourceSnapshot {
  const audioStore = store.audio;
  const recordingStore = audioStore.recording;

  const noteObservable = store.notes.getNoteObservableById({ noteId });
  const noteRecording = store.audioRecordings.getNoteRecording({ noteId });
  const activeRecordingObservable = noteRecording.recording as AudioRecordingObservable | undefined;
  const recordingId = activeRecordingObservable?.id ?? null;
  const handoffTransition = audioStore.detectedMeetingHandoffTransition;
  const handoffTransitionPreviousRecording =
    handoffTransition &&
    handoffTransition.successorAudioRecordingId === recordingId &&
    handoffTransition.associatedNoteId === noteId
      ? noteRecording.recordings.find(
          recording => recording.id === handoffTransition.previousAudioRecordingId
        )
      : undefined;

  // While detected-meeting handoff is restarting, the active id already points
  // to the successor. Keep rendering the previous recording's transcript panel
  // with a short restart status until the successor produces transcript or the
  // restart message timeout elapses.
  const recordingObservable = handoffTransitionPreviousRecording ?? activeRecordingObservable;
  const persistedHandoffPreviousRecording =
    activeRecordingObservable?.meetingHandoffPreviousAudioRecordingId
      ? noteRecording.recordings.find(
          recording =>
            recording.id === activeRecordingObservable.meetingHandoffPreviousAudioRecordingId
        )
      : undefined;
  const handoffPreviousRecording = handoffTransitionPreviousRecording
    ? undefined
    : persistedHandoffPreviousRecording;
  const handoffPreviousLiveTranscriptPartials =
    handoffPreviousRecording?.liveTranscriptPartials ?? [];
  const handoffSuccessorDisplayOffset = handoffPreviousRecording
    ? getNextTranscriptDisplayTurnOffset({
        finalizedTurns: handoffPreviousRecording.transcriptTurns,
        livePartialTurns: handoffPreviousLiveTranscriptPartials,
      })
    : 0;
  const transcriptTurns = handoffPreviousRecording
    ? [
        ...handoffPreviousRecording.transcriptTurns,
        ...offsetTranscriptTurns({
          transcriptTurns: recordingObservable?.transcriptTurns ?? [],
          finalizedTurnOffset: handoffSuccessorDisplayOffset,
        }),
      ]
    : combineSequentialTranscriptTurnGroups(
        recordingObservable ? [recordingObservable.transcriptTurns] : []
      );
  const recordingLiveTranscriptPartials = recordingObservable?.liveTranscriptPartials ?? [];
  const liveTranscriptPartials = handoffPreviousRecording
    ? [
        ...handoffPreviousLiveTranscriptPartials,
        ...offsetLiveTranscriptPartials({
          liveTranscriptPartials: recordingLiveTranscriptPartials,
          finalizedTurnOffset: handoffSuccessorDisplayOffset,
        }),
      ]
    : offsetLiveTranscriptPartials({
        liveTranscriptPartials: recordingLiveTranscriptPartials,
        finalizedTurnOffset: 0,
      });
  const currentUser = resolveCurrentTranscriptSpeakerUser(store, {
    allowUniqueDisplayNameFallback: true,
  });
  const transcriptEntries = normalizeTranscriptEntries(transcriptTurns, liveTranscriptPartials, {
    currentUser,
  });

  const transcript: AudioCaptureTranscriptState = {
    entries: transcriptEntries,
    fullText: buildFullTranscript({
      finalTurns: transcriptTurns,
      livePartialTurns: liveTranscriptPartials,
      currentUser,
    }),
    hasTranscript: transcriptEntries.length > 0,
    handoffStatusLabel: handoffTransitionPreviousRecording
      ? "Restarting Voice Mode to enable speaker attribution"
      : null,
    isDisconnected: recordingObservable?.id
      ? recordingStore.getTranscriptionStatus(recordingObservable.id) ===
        TranscriptionStatus.Disconnected
      : false,
    isCompromised: recordingObservable?.id
      ? recordingStore.getTranscriptionStatus(recordingObservable.id) ===
        TranscriptionStatus.Compromised
      : false,
    isTranscriptionUnsupported: recordingStore.transcriptIsUnsupported,
  };

  const microphone: AudioCaptureMicrophoneState = {
    label: recordingStore.activeAudioInputDeviceLabel,
    isDisconnected: recordingStore.isMicrophoneDisconnected,
    isAudioSilent: recordingStore.isAudioSilent,
  };

  const snapshot = recordingId ? audioStore.pipeline.getSnapshot(recordingId) : null;
  const pipelineUi = projectCapturePipelineUiState({
    snapshot,
    recording: recordingObservable ?? null,
    isInitializing: handoffTransitionPreviousRecording ? false : recordingStore.isInitializing,
    isRecordingPaused: recordingStore.isPaused,
  });

  const noteContentReadiness = getNoteSuggestionContentReadiness(noteObservable);
  const canProcessNoteContent = noteContentReadiness.state === NoteSuggestionContentState.Ready;

  const canProcess = canProcessRecording({
    phase: pipelineUi.phase,
    isMicrophoneDisconnected: recordingStore.isMicrophoneDisconnected,
    canProcessNoteContent,
  });

  const primaryAction = deriveToolbarPrimaryAction({
    canProcess,
    isProcessing: pipelineUi.isProcessing,
    retryTargets: pipelineUi.retryTargets,
  });
  const toolbar = {
    processingLabel: pipelineUi.processingLabel,
    primaryAction,
    showCancel: pipelineUi.isFinished && !pipelineUi.isProcessing,
    retryTargets: {
      upload: pipelineUi.retryTargets.upload,
      transform: pipelineUi.retryTargets.transform || pipelineUi.retryTargets.retranscribe,
      retryAll: pipelineUi.retryTargets.retryAll,
    },
  };

  const recordingBase: Omit<AudioCaptureRecordingState, "waveform"> = {
    phase: pipelineUi.phase,
    durationLabel: resolveAudioRecordingDurationLabel({
      activeRecording: activeRecordingObservable,
      noteRecordings: noteRecording.recordings,
      handoffTransition,
      noteId,
      fallbackDurationLabel: recordingObservable?.formattedDuration ?? "00:00",
    }),
    transcript,
    microphone,
    isProcessing: pipelineUi.isProcessing,
    isFinished: pipelineUi.isFinished,
    activeMicrophoneLabel: recordingStore.activeAudioInputDeviceLabel,
    noteContentState: noteContentReadiness.state,
    toolbar,
    failureMessage: pipelineUi.failureMessage,
  };

  const ui = {
    showOnboardingTooltip: audioStore.ui.showOnboardingTooltip,
    shouldShowDesktopDownloadNotice: !clientEnvModule.isDesktop(),
  };

  const environment: AudioCaptureEnvironmentState = {
    isAdmin: isMemAccount(store.account.myAccount),
    isDesktop: store.interface.isDesktop,
  };

  return {
    recording: recordingBase,
    ui,
    environment,
    visualizer: recordingStore.visualizer,
    recordingId,
  };
}

/**
 * Provides the transcript renderer with the signed-in user's stable identity.
 *
 * Meeting-window capture can report the user as a platform participant, so the
 * bubble renderer needs account identity to keep the user's own speech on the
 * right even when the recorder labels the turn as a participant.
 */
function resolveCurrentTranscriptSpeakerUser(
  store: AppStore,
  {
    allowUniqueDisplayNameFallback = false,
  }: {
    allowUniqueDisplayNameFallback?: boolean;
  } = {}
): TranscriptSpeakerCurrentUser {
  const myAccount = store.account.myAccount;

  return {
    email: myAccount.profileEmailAddress,
    displayName: myAccount.profileDisplayName,
    allowUniqueDisplayNameFallback,
  };
}
